import React, { useEffect, useRef, useState } from 'react';
import Papa from 'papaparse';
import { Plus, Save, Trash2, GripVertical, Database, Upload, Info, X } from 'lucide-react';
import LoadingSpinner from '../LoadingSpinner';
import { holdingsService } from '../../services/holdingsService';
import {
  holdingMarketValue,
  holdingGainLossPct,
  portfolioPercent,
  portfolioValue,
  equitiesTotals,
  positionHoldings,
  toNumber,
} from '../../utils/calc';
import { buildImportPlan, mapCsvHeaders, parseCsvRow } from '../../utils/holdingsImport';
import { formatCurrency, formatGainLoss, formatPercent } from '../../utils/format';

const emptyHolding = () => ({
  name: '',
  ticker: '',
  shares: '',
  costBasis: '',
  currentPrice: '',
  marketValue: '',
  dividendYield: '',
  cashDividends: '',
  reinvestedDividends: '',
});

const val = (v) => (v === null || v === undefined ? '' : v);

function ImportPreviewModal({ plan, onClose, onApply, applying }) {
  if (!plan) return null;

  return (
    <div className="a-modal-backdrop" onClick={onClose} role="presentation">
      <div
        className="a-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="import-preview-title"
      >
        <div className="a-modal-head">
          <h3 id="import-preview-title">Import preview</h3>
          <button type="button" className="a-icon-btn" onClick={onClose} title="Close">
            <X size={18} />
          </button>
        </div>

        {!plan.ok ? (
          <div className="a-modal-body">
            <p className="a-modal-error">Cannot import this file:</p>
            <ul className="a-import-list">
              {plan.errors.map((err) => (
                <li key={err}>{err}</li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="a-modal-body">
            <div className="a-import-stats">
              <span className="a-import-stat">
                <strong>{plan.updates.length}</strong> update{plan.updates.length === 1 ? '' : 's'}
              </span>
              <span className="a-import-stat">
                <strong>{plan.adds.length}</strong> new
              </span>
              {plan.skipped.length > 0 && (
                <span className="a-import-stat muted">
                  <strong>{plan.skipped.length}</strong> skipped
                </span>
              )}
            </div>

            {plan.updates.length > 0 && (
              <>
                <p className="a-modal-section-label">Updates (matched by ticker or name)</p>
                <ul className="a-import-list compact">
                  {plan.updates.map((u) => (
                    <li key={u.key}>
                      <span className="a-import-ticker">{u.label}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}

            {plan.adds.length > 0 && (
              <>
                <p className="a-modal-section-label">New positions</p>
                <ul className="a-import-list compact">
                  {plan.adds.map((a) => (
                    <li key={a.key}>
                      <span className="a-import-ticker">{a.label}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}

            {plan.skipped.length > 0 && (
              <>
                <p className="a-modal-section-label">Skipped</p>
                <ul className="a-import-list compact muted">
                  {plan.skipped.map((s, i) => (
                    <li key={`${s.label}-${i}`}>
                      {s.label}: {s.reason}
                    </li>
                  ))}
                </ul>
              </>
            )}

            <p className="a-hint" style={{ marginTop: 14 }}>
              Positions not in the CSV are left unchanged. Empty cells do not overwrite existing
              values.
            </p>
          </div>
        )}

        <div className="a-modal-foot">
          <button type="button" className="a-btn a-btn-ghost" onClick={onClose} disabled={applying}>
            Cancel
          </button>
          {plan.ok && (
            <button
              type="button"
              className="a-btn a-btn-primary"
              onClick={onApply}
              disabled={applying || (plan.updates.length === 0 && plan.adds.length === 0)}
            >
              {applying ? 'Importing...' : 'Apply import'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function HoldingsTab({ onStatus }) {
  const [holdings, setHoldings] = useState([]);
  const [cash, setCash] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importPlan, setImportPlan] = useState(null);
  const [dragIndex, setDragIndex] = useState(null);
  const [needsNormalize, setNeedsNormalize] = useState(false);
  const fileInputRef = useRef(null);

  const reloadHoldings = async () => {
    const [data, meta] = await Promise.all([
      holdingsService.getAllHoldings(),
      holdingsService.getFundMeta(),
    ]);
    setHoldings(data.map((h) => ({ ...h, name: h.name ?? h.equities ?? '' })));
    setCash(meta?.cash ?? '');
  };

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [data, meta] = await Promise.all([
          holdingsService.getAllHoldings(),
          holdingsService.getFundMeta(),
        ]);
        if (!active) return;
        const legacy = data.some(
          (h) =>
            typeof h.costBasis === 'string' ||
            'totalgainloss' in h ||
            'portfolioPercent' in h ||
            'equities' in h
        );
        setNeedsNormalize(legacy);
        setHoldings(data.map((h) => ({ ...h, name: h.name ?? h.equities ?? '' })));
        setCash(meta?.cash ?? '');
      } catch (err) {
        onStatus(`Error loading holdings: ${err.message}`, true);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [onStatus]);

  const positions = positionHoldings(holdings);

  const updateField = (index, field, value) => {
    setHoldings((prev) => {
      const position = positions[index];
      const fullIndex = prev.findIndex((h) => h === position);
      if (fullIndex < 0) return prev;
      const next = [...prev];
      next[fullIndex] = { ...next[fullIndex], [field]: value };
      return next;
    });
  };

  const addHolding = () => setHoldings((prev) => [...prev, emptyHolding()]);

  const removeHolding = async (index) => {
    const holding = positions[index];
    if (holding.id) {
      try {
        await holdingsService.deleteHolding(holding.id);
        onStatus('Holding deleted.');
      } catch (err) {
        onStatus(`Error deleting: ${err.message}`, true);
        return;
      }
    }
    setHoldings((prev) => prev.filter((h) => h !== holding));
  };

  const saveAll = async () => {
    try {
      setSaving(true);
      onStatus('Saving...');
      await holdingsService.saveAllHoldings(holdings);
      await holdingsService.setCash(cash);
      await reloadHoldings();
      onStatus('All changes saved.');
    } catch (err) {
      onStatus(`Error saving: ${err.message}`, true);
    } finally {
      setSaving(false);
    }
  };

  const normalize = async () => {
    if (
      !window.confirm(
        'Convert legacy holdings to the new numeric format? This moves Cash into the fund total and removes the old summary rows. Run once.'
      )
    ) {
      return;
    }
    try {
      onStatus('Normalizing data...');
      const result = await holdingsService.normalizeLegacyData();
      await reloadHoldings();
      setNeedsNormalize(false);
      onStatus(`Normalized ${result.converted} holdings, removed ${result.removed} legacy rows.`);
    } catch (err) {
      onStatus(`Normalize error: ${err.message}`, true);
    }
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        try {
          const headerMap = mapCsvHeaders(results.meta?.fields || []);
          if (!headerMap.ticker && !headerMap.name) {
            onStatus('CSV must include a name or ticker column.', true);
            return;
          }

          const parsedRows = results.data
            .map((row) => parseCsvRow(row, headerMap))
            .filter((row) => Object.keys(row._present).length > 0);

          if (parsedRows.length === 0) {
            onStatus('No valid rows found in CSV.', true);
            return;
          }

          const plan = buildImportPlan(parsedRows, holdings);
          setImportPlan(plan);
        } catch (err) {
          onStatus(`CSV parse error: ${err.message}`, true);
        } finally {
          if (fileInputRef.current) fileInputRef.current.value = '';
        }
      },
      error: (err) => onStatus(`CSV parse error: ${err.message}`, true),
    });
  };

  const applyImport = async () => {
    if (!importPlan?.ok) return;
    try {
      setImporting(true);
      const result = await holdingsService.importMerge(importPlan);
      await reloadHoldings();
      setImportPlan(null);
      onStatus(`Imported: ${result.updated} updated, ${result.added} added.`);
    } catch (err) {
      onStatus(`Import error: ${err.message}`, true);
    } finally {
      setImporting(false);
    }
  };

  const onDrop = (index) => {
    if (dragIndex === null || dragIndex === index) return;
    setHoldings((prev) => {
      const fromHolding = positions[dragIndex];
      const toHolding = positions[index];
      const fromIndex = prev.findIndex((h) => h === fromHolding);
      const toIndex = prev.findIndex((h) => h === toHolding);
      if (fromIndex < 0 || toIndex < 0) return prev;
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
    setDragIndex(null);
  };

  if (loading) return <LoadingSpinner text="Loading holdings..." />;

  const total = portfolioValue(positions, cash);
  const totals = equitiesTotals(positions);
  const equitiesPct = total ? (totals.marketValue / total) * 100 : null;
  const equitiesGl = totals.costBasis
    ? ((totals.marketValue - totals.costBasis) / totals.costBasis) * 100
    : null;

  return (
    <div>
      <div className="a-toolbar">
        <h2 className="a-section-title">Positions</h2>
        <div className="a-toolbar-actions">
          {needsNormalize && (
            <button className="a-btn a-btn-warn" onClick={normalize}>
              <Database size={16} /> Normalize legacy data
            </button>
          )}
          <button className="a-btn a-btn-ghost" onClick={() => fileInputRef.current?.click()}>
            <Upload size={16} /> Import CSV
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFile}
            style={{ display: 'none' }}
          />
          <button className="a-btn a-btn-ghost" onClick={addHolding}>
            <Plus size={16} /> Add holding
          </button>
        </div>
      </div>

      <p className="a-hint" style={{ marginBottom: 14 }}>
        <Info size={14} /> CSV columns: name, ticker, shares, cost_basis, current_price,
        market_value, dividend_yield, cash_dividends, reinvested_dividends. Matches on ticker
        (then name); updates existing rows and adds new ones.
      </p>

      <div className="holdings-table-wrap">
        <table className="holdings-table">
          <thead>
            <tr>
              <th className="drag-col" />
              <th>Company</th>
              <th>Ticker</th>
              <th className="num">Shares</th>
              <th className="num">Price</th>
              <th className="num">Market value</th>
              <th className="num">Cost basis</th>
              <th className="num">Div yield %</th>
              <th className="num">Cash div</th>
              <th className="num">Reinv div</th>
              <th className="num">% port</th>
              <th className="num">Gain / loss</th>
              <th className="act-col" />
            </tr>
          </thead>
          <tbody>
            {positions.map((holding, index) => {
              const mv = holdingMarketValue(holding);
              const gl = holdingGainLossPct(holding);
              const pct = portfolioPercent(holding, positions, cash);
              const autoMv =
                toNumber(holding.shares) !== null && toNumber(holding.currentPrice) !== null;
              return (
                <tr
                  key={holding.id || `new-${index}`}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => onDrop(index)}
                >
                  <td
                    className="drag-col"
                    draggable
                    onDragStart={() => setDragIndex(index)}
                    title="Drag to reorder"
                  >
                    <GripVertical size={15} />
                  </td>
                  <td>
                    <input
                      className="cell-input"
                      value={val(holding.name)}
                      onChange={(e) => updateField(index, 'name', e.target.value)}
                      placeholder="Company name"
                    />
                  </td>
                  <td>
                    <input
                      className="cell-input"
                      value={val(holding.ticker)}
                      onChange={(e) => updateField(index, 'ticker', e.target.value.toUpperCase())}
                      placeholder="TICK"
                      style={{ width: 70 }}
                    />
                  </td>
                  <td className="num">
                    <input
                      className="cell-input num"
                      type="number"
                      value={val(holding.shares)}
                      onChange={(e) => updateField(index, 'shares', e.target.value)}
                      placeholder="0"
                    />
                  </td>
                  <td className="num">
                    <input
                      className="cell-input num"
                      type="number"
                      value={val(holding.currentPrice)}
                      onChange={(e) => updateField(index, 'currentPrice', e.target.value)}
                      placeholder="0.00"
                    />
                  </td>
                  <td className="num">
                    <input
                      className="cell-input num"
                      type="number"
                      value={autoMv ? Math.round(mv) : val(holding.marketValue)}
                      onChange={(e) => updateField(index, 'marketValue', e.target.value)}
                      disabled={autoMv}
                      title={autoMv ? 'Computed from shares x price' : ''}
                      placeholder="0"
                    />
                  </td>
                  <td className="num">
                    <input
                      className="cell-input num"
                      type="number"
                      value={val(holding.costBasis)}
                      onChange={(e) => updateField(index, 'costBasis', e.target.value)}
                      placeholder="0"
                    />
                  </td>
                  <td className="num">
                    <input
                      className="cell-input num"
                      type="number"
                      value={val(holding.dividendYield)}
                      onChange={(e) => updateField(index, 'dividendYield', e.target.value)}
                      placeholder="--"
                    />
                  </td>
                  <td className="num">
                    <input
                      className="cell-input num"
                      type="number"
                      value={val(holding.cashDividends)}
                      onChange={(e) => updateField(index, 'cashDividends', e.target.value)}
                      placeholder="--"
                    />
                  </td>
                  <td className="num">
                    <input
                      className="cell-input num"
                      type="number"
                      value={val(holding.reinvestedDividends)}
                      onChange={(e) => updateField(index, 'reinvestedDividends', e.target.value)}
                      placeholder="--"
                    />
                  </td>
                  <td className="cell-computed">{formatPercent(pct)}</td>
                  <td
                    className={`cell-computed ${
                      gl === null ? '' : gl < 0 ? 'a-neg' : 'a-pos'
                    }`}
                  >
                    {formatGainLoss(gl)}
                  </td>
                  <td className="act-col">
                    <button
                      className="a-icon-btn danger"
                      onClick={() => removeHolding(index)}
                      title="Remove"
                    >
                      <Trash2 size={15} />
                    </button>
                  </td>
                </tr>
              );
            })}
            {positions.length === 0 && (
              <tr>
                <td colSpan={13} className="a-empty-cell">
                  No holdings yet. Click &ldquo;Add holding&rdquo; or import a CSV.
                </td>
              </tr>
            )}
          </tbody>
          {positions.length > 0 && (
            <tfoot>
              <tr>
                <td />
                <td>Equities total</td>
                <td />
                <td />
                <td />
                <td className="num">{formatCurrency(totals.marketValue)}</td>
                <td className="num">{formatCurrency(totals.costBasis)}</td>
                <td />
                <td className="num">{formatCurrency(totals.cashDividends)}</td>
                <td className="num">{formatCurrency(totals.reinvestedDividends)}</td>
                <td className="num">{formatPercent(equitiesPct)}</td>
                <td className={`num ${equitiesGl === null ? '' : equitiesGl < 0 ? 'a-neg' : 'a-pos'}`}>
                  {formatGainLoss(equitiesGl)}
                </td>
                <td />
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      <div className="holdings-actionbar">
        <div className="holdings-cash">
          <label>Cash balance ($)</label>
          <input
            className="a-input"
            type="number"
            value={val(cash)}
            onChange={(e) => setCash(e.target.value)}
            placeholder="0"
          />
        </div>
        <div className="holdings-total">
          <span className="lbl">Total portfolio value</span>
          <span className="val">{formatCurrency(total)}</span>
        </div>
        <div className="spacer" />
        <button className="a-btn a-btn-primary" onClick={saveAll} disabled={saving}>
          <Save size={16} /> {saving ? 'Saving...' : 'Save all changes'}
        </button>
      </div>

      <ImportPreviewModal
        plan={importPlan}
        onClose={() => setImportPlan(null)}
        onApply={applyImport}
        applying={importing}
      />
    </div>
  );
}

export default HoldingsTab;
